import React from 'react';
import { Col, Row } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, Radio, RadioGroup, TextField } from 'ui-kit';
import FormControl from 'ui-kit/FormControl';
import {
  FieldError,
  FieldErrorsImpl,
  FieldValues,
  Merge,
  useFormContext,
  UseFormSetError,
} from 'react-hook-form';
import clsx from 'clsx';

interface Props {
  value: { label: string }[];
  onChange: (value: { label: string }[]) => void;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | any;
  setError: UseFormSetError<FieldValues>;
}

export default function ExtraFields({
  error,
  value = [], // Default to empty array
  onChange,
  ...field
}: Props) {
  const { setValue, watch } = useFormContext();
  const includeExtraFields = watch('includeExtraFields');

  const handleChange = (e) => {
    const isIncluded = e.target.value;
    setValue('includeExtraFields', isIncluded);
    // Initialize with one field if Yes, clear if No
    onChange(isIncluded ? [{ label: '' }] : []);
  };

  const handleFieldChange = (index, newValue) => {
    const newList = [...value];
    newList[index] = { ...newList[index], label: newValue };
    onChange(newList);
  };

  const addField = () => {
    onChange([...value, { label: '' }]);
  };

  // Logic to remove a field by its index
  const removeField = (indexToRemove) => {
    const newList = value.filter((_, index) => index !== indexToRemove);
    onChange(newList);
  };

  return (
    <Row gutter={[0, 24]}>
      <Col xs={24}>
        <FormControl
          label="Requires additional information from guest"
          helperText="This is the label for the field your guest will see."
        >
          <RadioGroup value={includeExtraFields} onChange={handleChange}>
            <div>
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </div>
          </RadioGroup>
        </FormControl>
      </Col>

      {includeExtraFields && (
        <>
          {value.map((item, index) => {
            const fieldErrorObj = (error as any)?.[index]?.label;
            const hasError = !!fieldErrorObj;
            const errorMessage = fieldErrorObj?.message;

            return (
              <Col xs={24} key={index}>
                <FormControl
                  label={index === 0 ? 'Field name' : `Field name ${index + 1}`}
                  required
                  error={hasError}
                  fullWidth
                  errorMessage={errorMessage}
                >
                  <Row gutter={8} align="middle">
                    <Col xs={18} md={11}>
                      <TextField
                        error={hasError}
                        value={item.label || ''}
                        onChange={(e) => handleFieldChange(index, e.target.value)}
                        {...field}
                      />
                    </Col>
                    {/* Only show delete if there is more than one field, 
                      or keep it always if you want to allow 0 fields */}
                    <Col xs={2}>
                      <Button onClick={() => removeField(index)} title="Remove field">
                        <DeleteOutlined />
                      </Button>
                    </Col>
                  </Row>
                </FormControl>
              </Col>
            );
          })}
          <Col xs={24}>
            <Button
              variant="outlined"
              size="small"
              className={clsx('DelegatesList__addButton', value.length > 0 && 'mt-8')}
              onClick={addField}
            >
              Add field
            </Button>
          </Col>
        </>
      )}
    </Row>
  );
}
